import { useEffect, useRef } from "react";
import {
  USER_VERIFIED_STATUSES,
  type UserVerificationStatus,
} from "@/features/account/types/Verification";
import { usePreviousValue } from "@picklebet/ui";
import type { Maybe } from "types/utilities";
import { useVerificationReadiness } from "./firestore/user/useVerificationReadiness";
import { useProfile } from "./firestore/user/useProfile";

// unknown is used for cases where verification was already passed or failed
export type VerificationOutcome = "passed" | "failed" | "unknown";

// https://puntaa.atlassian.net/browse/PKB-13838.
// the verification status can land shortly after readiness flips, so give it
// time to arrive before treating UNVERIFIED as a failed background check
const READY_SETTLE_MS = 3_000;

export const useOnTransitionedVerificationStatus = (
  onTransition: (outcome: VerificationOutcome) => void,
) => {
  const { profile } = useProfile();
  const userVerificationStatus = profile?.userVerificationStatus;

  const { ready, isLoading: readinessLoading } = useVerificationReadiness();
  const hasTransitionedRef = useRef(false);

  const previousStatus = usePreviousValue<Maybe<UserVerificationStatus>>(
    userVerificationStatus,
  );

  useEffect(() => {
    // don't run again if we have already sent the verification attempted event
    if (hasTransitionedRef.current) return;

    const wasUnverified =
      previousStatus == null || previousStatus === "UNVERIFIED";
    const isNowVerified =
      typeof userVerificationStatus !== "undefined" &&
      USER_VERIFIED_STATUSES.includes(userVerificationStatus);
    const isAlreadyAttempted =
      typeof userVerificationStatus !== "undefined" &&
      userVerificationStatus !== "UNVERIFIED";

    const transition = (outcome: VerificationOutcome) => {
      hasTransitionedRef.current = true;
      onTransition(outcome);
    };

    if (wasUnverified && isNowVerified) {
      transition("passed");
    } else if (wasUnverified && isAlreadyAttempted) {
      transition("failed");
    } else if (!wasUnverified && isAlreadyAttempted) {
      transition("unknown");
    } else if (
      userVerificationStatus === "UNVERIFIED" &&
      !readinessLoading &&
      ready
    ) {
      // users stay UNVERIFIED when the background checks fail, readiness only
      // flips once those checks are done and manual verification is available
      const timeout = setTimeout(() => transition("failed"), READY_SETTLE_MS);
      return () => clearTimeout(timeout);
    }
  }, [userVerificationStatus, ready, readinessLoading]);
};
